import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  LockReset,
  Search,
  Security,
} from '@mui/icons-material';
import { usersAPI } from '../../services/api';

const emptyUserForm = {
  username: '',
  email: '',
  password: '',
  role: 'student',
  reference_id: '',
  is_active: true,
};

const roleOptions = ['superadmin', 'admin', 'teacher', 'student', 'parent'];

const roleColor = (role) => {
  if (role === 'superadmin') return 'error';
  if (role === 'admin') return 'primary';
  if (role === 'teacher') return 'info';
  if (role === 'student') return 'success';
  return 'default';
};

const dateLabel = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyUserForm);
  const [resetForm, setResetForm] = useState({ user_id: '', username: '', new_password: '' });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showMessage = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (roleFilter) params.role = roleFilter;
      if (activeFilter !== '') params.is_active = activeFilter;

      const response = await usersAPI.getAll(params);
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      showMessage(error.response?.data?.message || 'Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(loadUsers, 250);
    return () => clearTimeout(timer);
  }, [search, roleFilter, activeFilter]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((user) => Number(user.is_active) === 1).length,
    admins: users.filter((user) => ['admin', 'superadmin'].includes(user.role)).length,
  }), [users]);

  const openCreateDialog = () => {
    setEditingUser(null);
    setForm(emptyUserForm);
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setForm({
      username: user.username || '',
      email: user.email || '',
      password: '',
      role: user.role || 'student',
      reference_id: user.reference_id || '',
      is_active: Number(user.is_active) === 1,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setForm(emptyUserForm);
  };

  const handleFormChange = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    if (!form.username.trim() || !form.email.trim()) {
      showMessage('Username and email are required', 'error');
      return;
    }

    if (!editingUser && !form.password.trim()) {
      showMessage('Password is required for new users', 'error');
      return;
    }

    try {
      setSaving(true);

      if (editingUser) {
        await usersAPI.update(editingUser.id, {
          username: form.username.trim(),
          email: form.email.trim(),
          reference_id: form.reference_id || null,
          is_active: form.is_active ? 1 : 0,
        });

        if (editingUser.role !== form.role) {
          await usersAPI.changeRole(editingUser.id, form.role);
        }

        showMessage('User updated successfully');
      } else {
        await usersAPI.create({
          username: form.username.trim(),
          email: form.email.trim(),
          password: form.password,
          role: form.role,
          reference_id: form.reference_id || null,
          is_active: form.is_active ? 1 : 0,
        });
        showMessage('User created successfully');
      }

      closeDialog();
      loadUsers();
    } catch (error) {
      showMessage(error.response?.data?.message || 'Failed to save user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openResetDialog = (user) => {
    setResetForm({ user_id: user.id, username: user.username, new_password: '' });
    setResetDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (resetForm.new_password.length < 6) {
      showMessage('Password must be at least 6 characters', 'error');
      return;
    }

    try {
      setSaving(true);
      await usersAPI.resetPassword(resetForm.user_id, resetForm.new_password);
      showMessage('Password reset successfully');
      setResetDialogOpen(false);
      setResetForm({ user_id: '', username: '', new_password: '' });
    } catch (error) {
      showMessage(error.response?.data?.message || 'Failed to reset password', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.username}"?`)) return;

    try {
      await usersAPI.delete(user.id);
      showMessage('User deleted successfully');
      loadUsers();
    } catch (error) {
      showMessage(error.response?.data?.message || 'Failed to delete user', 'error');
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create accounts, manage access, and reset passwords.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
          Add User
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total users', value: stats.total },
          { label: 'Active users', value: stats.active },
          { label: 'Admin users', value: stats.admins },
        ].map((item) => (
          <Grid item xs={12} md={4} key={item.label}>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {item.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            placeholder="Search username or email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Role"
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="">All roles</MenuItem>
            {roleOptions.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            value={activeFilter}
            onChange={(event) => setActiveFilter(event.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">Active</MenuItem>
            <MenuItem value="0">Inactive</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Reference ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length > 0 ? (
              users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Typography fontWeight={700}>{user.username}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={['admin', 'superadmin'].includes(user.role) ? <Security /> : undefined}
                      label={user.role}
                      size="small"
                      color={roleColor(user.role)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{user.reference_id || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={Number(user.is_active) === 1 ? 'Active' : 'Inactive'}
                      size="small"
                      color={Number(user.is_active) === 1 ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{dateLabel(user.last_login)}</TableCell>
                  <TableCell>{dateLabel(user.created_at)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="Edit user" onClick={() => openEditDialog(user)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="Reset password" onClick={() => openResetDialog(user)}>
                      <LockReset fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="Delete user" color="error" onClick={() => handleDelete(user)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  No users found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Username"
              name="username"
              value={form.username}
              onChange={handleFormChange}
              fullWidth
              required
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleFormChange}
              fullWidth
              required
            />
            {!editingUser && (
              <TextField
                label="Password"
                name="password"
                type="password"
                value={form.password}
                onChange={handleFormChange}
                fullWidth
                required
              />
            )}
            <TextField
              select
              label="Role"
              name="role"
              value={form.role}
              onChange={handleFormChange}
              fullWidth
            >
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Reference ID"
              name="reference_id"
              type="number"
              value={form.reference_id}
              onChange={handleFormChange}
              fullWidth
              helperText="Optional student or teacher ID linked to this account"
            />
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleFormChange}
                />
              }
              label="Active account"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Set a new password for {resetForm.username}.
            </Alert>
            <TextField
              label="New password"
              type="password"
              value={resetForm.new_password}
              onChange={(event) => setResetForm((prev) => ({ ...prev, new_password: event.target.value }))}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained" disabled={saving}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
